import React, { useEffect, useState } from "react";
import {
  getIdTokenResult,
  onIdTokenChanged,
  signOut,
} from "firebase/auth";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import { auth } from "./firebase";
import Dashboard from "./components/Dashboard";
import Products from "./components/Products";
import News from "./components/News";
import FAQ from "./components/FAQ";
import Scenarios from "./components/Scenarios";
import About from "./components/About";
import Contact from "./components/Contact";
import PublicTrackingPage from "./pages/PublicTrackingPage";
import Login from "./pages/Login";
import DriverManagerHome from "./pages/drivermanager/DriverManagerHome";
import SystemAdminHome from "./pages/systemadmin/SystemAdminHome";
import {
  INTERNAL_ENTRY_BASE,
  INTERNAL_ROUTES,
} from "./internalRoutes";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Terms from "./components/Terms";


const SYSTEM_ADMIN_ROLE = "systemAdmin";
const ORG_ADMIN_ROLE = "orgAdmin";
const OWNER_ROLE = "owner";
const DEPUTY_ROLE = "deputy";
const DRIVER_ROLE = "driver";

function hasSystemAdminClaims(claims) {
  return (
    claims?.systemAdmin === true ||
    claims?.role === SYSTEM_ADMIN_ROLE
  );
}

function hasOrgAdminClaims(user, claims) {
  if (
    (claims?.role === OWNER_ROLE || claims?.role === DEPUTY_ROLE) &&
    typeof claims?.orgId === "string" &&
    claims.orgId.trim()
  ) {
    return true;
  }

  return (
    Boolean(user?.uid) &&
    claims?.role === ORG_ADMIN_ROLE &&
    claims?.orgOwnerUid === user.uid
  );
}

function hasDriverClaims(user, claims) {
  return Boolean(user?.uid) && claims?.role === DRIVER_ROLE;
}

function useAuthSession() {
  const [session, setSession] = useState({
    loading: true,
    user: null,
    claims: null,
    error: "",
  });

  useEffect(() => {
    let active = true;

    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        if (active) {
          setSession({
            loading: false,
            user: null,
            claims: null,
            error: "",
          });
        }
        return;
      }

      try {
        const tokenResult = await getIdTokenResult(firebaseUser);

        if (active) {
          setSession({
            loading: false,
            user: firebaseUser,
            claims: tokenResult.claims || {},
            error: "",
          });
        }
      } catch (error) {
        console.error("[Auth] 無法讀取登入權限：", error);

        if (active) {
          setSession({
            loading: false,
            user: firebaseUser,
            claims: null,
            error: "無法確認目前登入權限，請重新登入。",
          });
        }
      }
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  return session;
}

function AuthLoading() {
  return (
    <div style={guardStyles.page}>
      <div style={guardStyles.card}>
        <div style={guardStyles.spinner} />
        <p style={guardStyles.text}>正在確認登入狀態…</p>
      </div>
    </div>
  );
}

function AccessDenied({ message }) {
  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("[Auth] 登出失敗：", error);
    }
  };

  return (
    <div style={guardStyles.page}>
      <div style={guardStyles.card}>
        <h1 style={guardStyles.title}>沒有存取權限</h1>

        <p style={guardStyles.text}>
          {message || "這個帳號沒有使用此功能的權限。"}
        </p>

        <button
          type="button"
          onClick={handleSignOut}
          style={guardStyles.button}
        >
          登出並返回登入
        </button>
      </div>
    </div>
  );
}

function RequireOrgAdmin({ children }) {
  const location = useLocation();
  const session = useAuthSession();

  if (session.loading) {
    return <AuthLoading />;
  }

  if (!session.user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  if (session.error) {
    return <AccessDenied message={session.error} />;
  }

  if (hasSystemAdminClaims(session.claims) || !hasOrgAdminClaims(session.user, session.claims)) {
    return (
      <AccessDenied
        message="此帳號沒有管理平台權限，請確認 Custom Claims。"
      />
    );
  }

  return children;
}

function RequireSystemAdmin({ children }) {
  const location = useLocation();
  const session = useAuthSession();

  if (session.loading) {
    return <AuthLoading />;
  }

  if (!session.user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  if (session.error) {
    return <AccessDenied message={session.error} />;
  }

  if (!hasSystemAdminClaims(session.claims)) {
    return (
      <AccessDenied message="此帳號沒有系統管理員權限。" />
    );
  }

  return children;
}

function RequireDriver({ children }) {
  const location = useLocation();
  const session = useAuthSession();

  if (session.loading) {
    return <AuthLoading />;
  }

  if (!session.user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  if (session.error) {
    return <AccessDenied message={session.error} />;
  }

  if (!hasDriverClaims(session.user, session.claims)) {
    return (
      <AccessDenied message="此帳號沒有司機入口的權限。" />
    );
  }

  return children;
}

function App() {
  const internalApp = (
    <RequireOrgAdmin>
      <DriverManagerHome />
    </RequireOrgAdmin>
  );
  const systemAdminApp = (
    <RequireSystemAdmin>
      <SystemAdminHome />
    </RequireSystemAdmin>
  );

  return (
    <Router>
      <Routes>
        <Route path="/" element={<Dashboard />} />

        <Route path="/login" element={<Login />} />

        <Route path="/track" element={<PublicTrackingPage />} />

        <Route path="/products" element={<Products />} />
        <Route path="/news" element={<News />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/scenarios" element={<Scenarios />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<Terms />} />

        <Route path="/systemadmin" element={systemAdminApp} />
        <Route path="/systemadmin/*" element={systemAdminApp} />
        
        <Route path={INTERNAL_ENTRY_BASE} element={internalApp} />
        <Route path={`${INTERNAL_ENTRY_BASE}/*`} element={internalApp} />

        {/* 舊網址只做相容導向，不再直接顯示內部系統。 */}
        <Route
          path="/drivermanager"
          element={<Navigate to={INTERNAL_ROUTES.home} replace />}
        />
        <Route
          path="/internalJob"
          element={<Navigate to={INTERNAL_ROUTES.job} replace />}
        />
        <Route
          path="/settings"
          element={<Navigate to={INTERNAL_ROUTES.settings} replace />}
        />
        <Route
          path="/drivers"
          element={<Navigate to={INTERNAL_ROUTES.driver} replace />}
        />

        <Route
          path="*"
          element={<Navigate to="/login" replace />}
        />
      </Routes>
    </Router>
  );
}

const guardStyles = {
  page: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    padding: 24,
    background: "#f5f7fb",
    fontFamily: 'Inter, "Noto Sans TC", sans-serif',
  },

  card: {
    width: "min(100%, 460px)",
    padding: 32,
    border: "1px solid rgba(148,163,184,.18)",
    borderRadius: 22,
    background: "#fff",
    boxShadow: "0 20px 60px rgba(15,23,42,.10)",
    textAlign: "center",
  },

  title: {
    margin: 0,
    color: "#111827",
    fontSize: 24,
    fontWeight: 900,
  },

  text: {
    margin: "12px 0 0",
    color: "#64748b",
    fontSize: 14,
    lineHeight: 1.7,
    fontWeight: 700,
  },

  button: {
    marginTop: 20,
    minHeight: 44,
    padding: "0 18px",
    border: 0,
    borderRadius: 12,
    background: "#111827",
    color: "#fff",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },

  spinner: {
    width: 28,
    height: 28,
    margin: "0 auto",
    border: "3px solid #e5e7eb",
    borderTopColor: "#e87820",
    borderRadius: "50%",
    animation: "auth-spin .8s linear infinite",
  },
};

export default App;
